
import { useState, useEffect, useCallback } from "react";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { useToast } from "@/hooks/use-toast";

export interface TwilioSettings {
  id: string;
  account_sid: string;
  auth_token: string;
  phone_number: string;
  messaging_service_sid: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useTwilioSettings = () => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();
  const [settings, setSettings] = useState<TwilioSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('twilio_settings')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching Twilio settings:', error);
        setError(error.message);
        return;
      }

      setSettings(data);
      setError(null);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient]);

  const updateSettings = async (updates: Partial<TwilioSettings>) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return false;

    if (!settings) {
      // Create new settings if none exist - ensure required fields are present
      if (!updates.account_sid || !updates.auth_token || !updates.phone_number) {
        toast({
          title: "Error",
          description: "Account SID, Auth Token, and Phone Number are required",
          variant: "destructive",
        });
        return false;
      }

      try {
        const newSettings = {
          account_sid: updates.account_sid,
          auth_token: updates.auth_token,
          phone_number: updates.phone_number,
          messaging_service_sid: updates.messaging_service_sid || null,
          is_active: updates.is_active ?? true
        };

        const { data, error } = await supabase
          .from('twilio_settings')
          .insert(newSettings)
          .select()
          .single();

        if (error) {
          console.error('Error creating Twilio settings:', error);
          toast({
            title: "Error",
            description: "Failed to create Twilio settings",
            variant: "destructive",
          });
          return false;
        }

        setSettings(data);
        toast({
          title: "Settings Created",
          description: "Twilio settings have been created successfully",
          className: "bg-status-success text-white"
        });
        return true;
      } catch (err) {
        console.error('Unexpected error:', err);
        return false;
      }
    }

    try {
      const { data, error } = await supabase
        .from('twilio_settings')
        .update(updates)
        .eq('id', settings.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating Twilio settings:', error);
        toast({
          title: "Error",
          description: "Failed to update Twilio settings",
          variant: "destructive",
        });
        return false;
      }

      setSettings(data);
      toast({
        title: "Settings Updated",
        description: "Twilio settings have been updated successfully",
        className: "bg-status-success text-white"
      });
      return true;
    } catch (err) {
      console.error('Unexpected error:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    loading,
    error,
    updateSettings,
    refetchSettings: fetchSettings
  };
};
